import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProjectWithUsers, useGetProjects } from '../modules/projects/projects.queries';
import { useStore } from '../store';

export type { ProjectWithUsers };

export interface UseCurrentProjectResult {
  currentProject: ProjectWithUsers | undefined;
  setProjectId: (projectId: string) => void;
  projects: ProjectWithUsers[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useCurrentProject(): UseCurrentProjectResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, projectId } = useParams();
  const { data, isLoading, isError, refetch } = useGetProjects();
  const projects = data ?? [];
  const selectedProjectId = useStore((state) => state.selectedProjectId);
  const setSelectedProjectId = useStore((state) => state.setSelectedProjectId);

  // Initialize default project on first load or when projects change
  useEffect(() => {
    if (!isLoading && projects.length > 0) {
      const projectExists = selectedProjectId && projects.some((p) => String(p.id) === selectedProjectId);
      if (!projectExists) {
        const firstProjectId = String(projects[0].id);
        setSelectedProjectId(firstProjectId);
      }
    }
  }, [projects, isLoading, selectedProjectId, setSelectedProjectId]);

  const currentProject = useMemo(() => {
    if (projects.length === 0) return undefined;
    const targetProjectId = projectId || selectedProjectId;
    return projects.find((p) => String(p.id) === targetProjectId);
  }, [projects, projectId, selectedProjectId]);

  const setProjectId = (newProjectId: string) => {
    console.log('setProjectId called:', { newProjectId, location: location.pathname });
    setSelectedProjectId(newProjectId);

    // If we are on the reports page, update URL
    if (location.pathname.includes('/reports/')) {
      navigate(`/${workspaceId}/reports/${newProjectId}${window.location.search}`);
    }
  };

  return {
    currentProject,
    setProjectId,
    projects,
    isLoading,
    isError,
    refetch,
  };
}
